/*
 * Button — the Hub's primary/secondary/danger button (DESIGN.md §8).
 *
 * - Primary: Studio Green field, Ink text, weight 800. The unmistakable CTA.
 * - Secondary: transparent, silver border, never competes with the primary.
 * - Danger: transparent, rose border + rose-deep text, for destructive actions.
 *
 * Tap scales to 0.97 (spring-snappy); keyboard focus shows the green glow ring
 * (.ldg-btn:focus-visible in theme.css). Both motion behaviors respect
 * prefers-reduced-motion via Framer Motion's useReducedMotion().
 *
 * 44px tall minimum — the one-thumb tap-target floor.
 */
import { motion, useReducedMotion } from 'framer-motion';
import type { HTMLMotionProps } from 'framer-motion';
import { SPRING_SNAPPY, tapScale } from '../lib/motion';

export type ButtonVariant = 'primary' | 'secondary' | 'danger';

interface ButtonProps extends HTMLMotionProps<'button'> {
  variant?: ButtonVariant;
  /** Stretch to the container width (the common mobile CTA layout). */
  fullWidth?: boolean;
}

const base: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 'var(--space-2)',
  minHeight: 'var(--tap-target)',
  padding: '0 var(--space-5)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid transparent',
  fontFamily: 'var(--font-body)',
  fontSize: 'var(--text-body-l)',
  fontWeight: 800,
  lineHeight: 1,
  cursor: 'pointer',
};

// Every variant is a two-tone vertical gradient (light top, deep bottom) with
// a glossy top highlight + a solid darker bottom lip, so the button reads as a
// rounded, raised, 3D candy button. Pressing scales it down (whileTap) so the
// "thickness" appears to compress.
//
// Primary's fill and shadow are read from theme.css (--btn-primary-bg /
// --btn-primary-shadow*) rather than hardcoded here, softened per Bobbie's
// 2026-07-13 feedback that it read as a hard, dark green plate: a light mint
// tint (#8FEC72) eases down to the Studio Green base color only, with the
// deeper shade living in the shadow/bottom-lip instead of the fill. Ink text
// (var(--primary-on), #0a0a0a) stays the text color; contrast against the
// lightest fill stop (#8FEC72) is ~13.6:1, and against the base green
// (#40D11F) it's ~9.8:1, both well past WCAG AA's 4.5:1. White text was
// considered and rejected: white against #8FEC72 only reaches ~1.5:1, so a
// tint light enough to read as "softened" cannot carry white text at AA.
const VARIANTS: Record<ButtonVariant, React.CSSProperties> = {
  primary: {
    background: 'var(--btn-primary-bg)',
    color: 'var(--primary-on)',
    boxShadow: 'var(--btn-primary-shadow)',
  },
  secondary: {
    background: 'linear-gradient(180deg, var(--paper) 0%, var(--silver-panel) 100%)',
    color: 'var(--ink)',
    borderColor: 'var(--silver-soft)',
    boxShadow:
      'inset 0 1px 0 rgba(255,255,255,0.65), 0 1px 0 var(--silver-edge), 0 8px 16px rgba(35,28,20,0.10)',
  },
  danger: {
    background: 'linear-gradient(180deg, #e95d78 0%, #d43a5e 100%)',
    color: '#FFFFFF',
    boxShadow:
      'inset 0 1px 0 rgba(255,255,255,0.34), 0 2px 0 #8B1A36, 0 8px 16px rgba(212,58,94,0.24)',
  },
};

// Disabled: drop saturation, no glow (DESIGN.md §8).
const disabledStyle: React.CSSProperties = {
  filter: 'saturate(0.4)',
  opacity: 0.6,
  cursor: 'not-allowed',
  boxShadow: 'none',
};

export function Button({
  variant = 'primary',
  fullWidth = false,
  disabled = false,
  style,
  children,
  ...rest
}: ButtonProps) {
  const reduce = useReducedMotion();
  return (
    <motion.button
      className="ldg-btn"
      data-variant={variant}
      disabled={disabled}
      whileTap={disabled ? undefined : tapScale(reduce)}
      transition={SPRING_SNAPPY}
      style={{
        ...base,
        ...VARIANTS[variant],
        ...(fullWidth ? { width: '100%' } : null),
        ...(disabled ? disabledStyle : null),
        ...style,
      }}
      {...rest}
    >
      {children}
    </motion.button>
  );
}

export default Button;
